/** English project examples (case studies). Outcomes stay illustrative unless backed by an evidence record. */
export type CaseStudy = {
  slug: string;
  client: string;
  industry: string;
  region: string;
  headline: string;
  summary: string;
  metrics: { label: string; value: string }[];
  problem: string;
  strategy: string[];
  result: string;
  duration: string;
  cover: string;
  images?: { src: string; alt: string }[];
  externalLinks?: { label: string; href: string }[];
};

export const CASES: CaseStudy[] = [
  {
    "slug": "danceamor-adelaide-seo-shopify",
    "client": "DanceAmor (Oscar Castellanos)",
    "industry": "Dance Studio / Shopify E-commerce",
    "region": "Adelaide, Australia",
    "headline": "DanceAmor: Website and Shopify Project Examples",
    "summary": "Project materials illustrating website, campaign or keyword-research work. No verified before-and-after outcome data is published.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Check crawl access, page titles and navigation against the actual website.",
      "Review product and collection information for accuracy and useful purchase details.",
      "Check eligible local business information and consistency across genuine profiles.",
      "Measure search performance only with dated, property-specific records."
    ],
    "result": "Correction — 31 October 2026: the previous numerical results and campaign timelines are withdrawn because supporting records are not on file. Available project images illustrate work examples only, not measured business outcomes.",
    "duration": "No verified campaign window published",
    "cover": "💃",
    "images": [
      {
        "src": "danceamor-1",
        "alt": "DanceAmor.com.au homepage hero — Bachata by the Beach Latin dance event in Adelaide"
      },
      {
        "src": "danceamor-2",
        "alt": "DanceAmor Shopify admin — Helen Dance Heels product listing for the dance shoes store"
      }
    ],
    "externalLinks": [
      {
        "label": "Visit DanceAmor.com.au",
        "href": "https://danceamor.com.au/"
      }
    ]
  },
  {
    "slug": "schildermij-swiss-painting-seo",
    "client": "schildermij.nl (Ciska van Selm)",
    "industry": "Fine Art / Portrait Painting",
    "region": "Switzerland",
    "headline": "schildermij.nl: A Practical SEO Review",
    "summary": "Illustrative practical guidance, not a verified client engagement or performance result.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Review the site’s real service area and audience before choosing search terms.",
      "Inspect indexability, readable portfolio descriptions and internal links.",
      "Choose appropriate author or artwork markup only where facts support it.",
      "Compare equal-length Search Console periods if verified account data becomes available."
    ],
    "result": "Correction — 31 October 2026: the previous client story and numerical results lack supporting records. This page now presents an illustrative workflow, not a completed engagement or verified outcome.",
    "duration": "No verified campaign window published",
    "cover": "🎨",
    "externalLinks": [
      {
        "label": "Visit schildermij.nl",
        "href": "https://schildermij.nl/"
      }
    ]
  },
  {
    "slug": "danse-evasion-social-media",
    "client": "Danse Évasion",
    "industry": "Dance School / Performing Arts",
    "region": "Switzerland",
    "headline": "Danse Évasion: Social Campaign Examples",
    "summary": "Project materials illustrating website, campaign or keyword-research work. No verified before-and-after outcome data is published.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Build a campaign calendar around genuine trial-class offers.",
      "Use consistent creative and accurate event details.",
      "Link the correct registration page and keep offer dates current.",
      "Assess reach and signups using actual platform exports and registration records."
    ],
    "result": "Correction — 31 October 2026: the previous numerical results and campaign timelines are withdrawn because supporting records are not on file. Available project images illustrate work examples only, not measured business outcomes.",
    "duration": "No verified campaign window published",
    "cover": "💃",
    "images": [
      {
        "src": "danse-evasion-1",
        "alt": "Danse Évasion Instagram post — free trial hip-hop classes Tuesday September 28"
      },
      {
        "src": "danse-evasion-2",
        "alt": "Danse Évasion Instagram campaign — free trial classes, hip-hop and breakdance, Lausanne"
      }
    ],
    "externalLinks": [
      {
        "label": "View Danse Évasion on Instagram",
        "href": "https://www.instagram.com/danseevasion/?hl=en"
      }
    ]
  },
  {
    "slug": "cbd-keyword-research",
    "client": "Rena's Organic",
    "industry": "Health & Wellness (CBD)",
    "region": "United States",
    "headline": "Rena’s Organic: Keyword Research Examples",
    "summary": "Project materials illustrating website, campaign or keyword-research work. No verified before-and-after outcome data is published.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Group candidate terms by audience intent and product category.",
      "Review search-volume estimates with their provider and date recorded.",
      "Map terms to actual pages and avoid unsupported health claims.",
      "Measure business outcomes separately from the keyword spreadsheet."
    ],
    "result": "Correction — 31 October 2026: the previous numerical results and campaign timelines are withdrawn because supporting records are not on file. Available project images illustrate work examples only, not measured business outcomes.",
    "duration": "No verified campaign window published",
    "cover": "🌿",
    "images": [
      {
        "src": "cbd-keywords-1",
        "alt": "Category-wise CBD keyword research spreadsheet showing keyword, search volume, and difficulty columns"
      },
      {
        "src": "cbd-keywords-2",
        "alt": "CBD keyword research deliverable with average monthly searches and competition score out of 100"
      }
    ],
    "externalLinks": [
      {
        "label": "View Rena's Organic",
        "href": "https://renasorganic.com/"
      }
    ]
  },
  {
    "slug": "germany-agency-seo",
    "client": "B2B agency example",
    "industry": "B2B Services",
    "region": "Germany",
    "headline": "B2B Agency SEO: An Illustrative Approach",
    "summary": "Illustrative practical guidance, not a verified client engagement or performance result.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Review service-page intent, crawl access and language equivalents.",
      "Prioritise technical findings with reproducible evidence.",
      "Write useful explanations and appropriate internal links.",
      "Measure qualified organic enquiries with a declared attribution method."
    ],
    "result": "Correction — 31 October 2026: the previous client story and numerical results lack supporting records. This page now presents an illustrative workflow, not a completed engagement or verified outcome.",
    "duration": "No verified campaign window published",
    "cover": "🇩🇪"
  },
  {
    "slug": "elementor-seo-build",
    "client": "Local service business example",
    "industry": "Home Services",
    "region": "Bangladesh",
    "headline": "Local Service Website: An Illustrative SEO Build",
    "summary": "Illustrative practical guidance, not a verified client engagement or performance result.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Create clear mobile-friendly service and contact pages.",
      "Check performance on the actual published pages.",
      "Maintain an eligible Google Business Profile with truthful details.",
      "Track real enquiries against a documented baseline."
    ],
    "result": "Correction — 31 October 2026: the previous client story and numerical results lack supporting records. This page now presents an illustrative workflow, not a completed engagement or verified outcome.",
    "duration": "No verified campaign window published",
    "cover": "🛠️"
  },
  {
    "slug": "saas-technical-audit",
    "client": "SaaS migration example",
    "industry": "SaaS",
    "region": "Singapore",
    "headline": "SaaS Migration SEO: A Diagnostic Workflow",
    "summary": "Illustrative practical guidance, not a verified client engagement or performance result.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Compare affected URLs before and after the migration.",
      "Inspect server responses and rendered content with URL Inspection.",
      "Check redirects, canonical URLs, language annotations and internal links.",
      "Monitor indexing and traffic without promising a recovery deadline."
    ],
    "result": "Correction — 31 October 2026: the previous client story and numerical results lack supporting records. This page now presents an illustrative workflow, not a completed engagement or verified outcome.",
    "duration": "No verified campaign window published",
    "cover": "⚙️"
  },
  {
    "slug": "ecommerce-local-bd",
    "client": "Ecommerce example",
    "industry": "Ecommerce",
    "region": "Bangladesh",
    "headline": "Bilingual Ecommerce SEO: An Illustrative Approach",
    "summary": "Illustrative practical guidance, not a verified client engagement or performance result.",
    "metrics": [],
    "problem": "The task is to review the relevant pages and information before deciding which improvements are justified. No verified starting traffic, rankings or lead baseline is published.",
    "strategy": [
      "Research Bangla and English questions from the actual audience.",
      "Create useful category and product pages with accurate information.",
      "Use appropriate product data and genuine reviews only.",
      "Measure search traffic and sales separately using verified tracking."
    ],
    "result": "Correction — 31 October 2026: the previous client story and numerical results lack supporting records. This page now presents an illustrative workflow, not a completed engagement or verified outcome.",
    "duration": "No verified campaign window published",
    "cover": "👗"
  }
];
